import { useApp } from '../lib/AppContext';
import { MOCK_CARDS } from '../lib/mockData';
import StoryCardItem from '../components/StoryCardItem';
import MicroTimeline from '../components/MicroTimeline';
import { TimelineEvent } from '../lib/types';
import Header from '../components/Header';

const ISSUE_TIMELINES: Record<string, TimelineEvent[]> = {
  'Justice': [
    {
      year: '2014',
      title: 'Ferguson uprising begins',
      description: 'Death of Mike Brown sparks nationwide protests.',
    },
    {
      year: '2016',
      title: 'BLM movement gains momentum',
      description: 'Protests spread across major US cities.',
    },
    {
      year: '2020',
      title: 'George Floyd\'s murder ignites global protests',
      description: 'Largest civil rights movement in US history.',
    },
  ],
  'Housing': [
    {
      year: '2017',
      title: 'Puerto Rico\'s debt crisis deepens',
      description: 'Hurricane Maria devastates island infrastructure.',
    },
    {
      year: '2018',
      title: 'Tax incentives attract wealthy investors',
      description: 'Act 20/22 brings mainlanders and crypto investors.',
    },
    {
      year: '2021',
      title: 'Rent prices surge 40%',
      description: 'Locals priced out; protests against gentrification.',
    },
  ],
  'Environment': [
    {
      year: '2018',
      title: 'IPCC report warns of 1.5°C threshold',
      description: 'Scientists say 12 years to limit climate catastrophe.',
    },
    {
      year: '2019',
      title: 'Youth climate strikes go global',
      description: 'Millions strike following Greta Thunberg\'s lead.',
    },
    {
      year: '2021',
      title: 'COP26 disappoints activists',
      description: 'Weak commitments fail to meet urgency of crisis.',
    },
  ],
};

const ISSUE_DESCRIPTIONS: Record<string, string> = {
  'Environment': 'Land, climate, and the systems that shape who controls and cares for it.',
  'Conflict': 'Music about war, resistance, and the human cost of violence.',
  'Education': 'Songs about access to knowledge, learning, and the politics of who gets taught what.',
  'Health': 'Music exploring wellbeing, mental health, healthcare access, and bodily autonomy.',
  'Housing': 'Songs about displacement, rising rents, and who gets to call a place home.',
  'Identity': 'Music exploring personal and collective identity, culture, and resistance.',
  'Justice': 'Songs that challenge police violence, mass incarceration, and the systems that uphold them.',
  'Media': 'Songs about propaganda, censorship, and controlling narratives.',
  'Migration': 'Songs about borders, displacement, refugees, and who gets to move freely.',
  'Work': 'Music examining labor, workers rights, and economic justice.',
};

export default function IssuePage() {
  const { currentCardId, navigateTo } = useApp();

  const issue = currentCardId || '';
  const relatedCards = MOCK_CARDS.filter(card =>
    card.broadCategory === issue
  );

  const timeline = ISSUE_TIMELINES[issue] || [];
  const description = ISSUE_DESCRIPTIONS[issue] || 'Explore stories about this issue.';

  const handleCardClick = (cardId: string) => {
    navigateTo('card-detail', cardId);
  };

  const handleTagClick = (tag: string) => {
    navigateTo('issue-page', tag);
  };

  return (
    <div className="min-h-screen bg-[#111111]" style={{ paddingBottom: 'calc(92px + env(safe-area-inset-bottom, 0px) + 16px)' }}>
      <Header
        mode="title"
        title={`Issue: ${issue}`}
        showBackButton
        onBackClick={() => navigateTo('home')}
      />

      <div className="px-6 py-6">
        <h2 className="text-[24px] font-semibold text-[#F5F5F5] mb-3">
          {issue}
        </h2>
        <p className="text-[14px] text-[#A0A0A0] leading-relaxed mb-6">
          {description}
        </p>

        {timeline.length > 0 && (
          <div className="mb-8">
            <div className="text-[12px] font-semibold text-[#FF6F61] uppercase tracking-wide mb-4">
              3-Step Micro Timeline
            </div>
            <div className="bg-[#181818] rounded-xl p-6">
              <MicroTimeline events={timeline} />
            </div>
          </div>
        )}
      </div>

      <div className="px-4 mb-4">
        <div className="text-[12px] font-semibold text-[#FF6F61] uppercase tracking-wide mb-3 px-2">
          Related Stories ({relatedCards.length})
        </div>
      </div>

      {relatedCards.length > 0 ? (
        <div className="space-y-0">
          {relatedCards.map(card => (
            <StoryCardItem
              key={card.id}
              card={card}
              onClick={() => handleCardClick(card.id)}
            />
          ))}
        </div>
      ) : (
        <div className="px-8 py-12 text-center">
          <p className="text-[14px] text-[#A0A0A0]">
            No stories found for this issue yet.
          </p>
        </div>
      )}
    </div>
  );
}
